<template>
    <div class="columns" v-if="initDone">
        <div class="column" v-if="publishedAcademyResults.length > 0">
            <div class="box is-flex is-align-items-center" v-for="result in publishedAcademyResults" :key="result.id">
                <div class="is-flex-grow-1">
                    <div class="has-text-weight-bold is-size-5">{{ result.name }}</div>
                    <div>
                        <span class="is-description">{{ translate("page.admin_academy_results.published_at") }}</span>
                        {{ autoConvertToTimezone(result.created) }}
                    </div>

                    <Accordion class="mt-2">
                        <template v-slot:title>{{ translate("page.admin_academy_results.click_to_expand_selected_rules") }}</template>
                        <template v-slot:content>
                            <ChosenRulesView :compact="true" :selected-rules="result.criteria.selectedRules" :read-only="true" />
                        </template>
                    </Accordion>
                </div>
                <div class="ml-5">
                    <LoadingButton
                        :is-loading="deletingInProgress === result.id"
                        class="button is-danger is-small"
                        @click="deletePublishedResult(result.id)"
                    >
                        <span class="icon">
                            <i class="fa fa-trash" />
                        </span>
                        <span>{{ translate("common.delete") }}</span>
                    </LoadingButton>
                </div>
            </div>
        </div>
        <div class="column is-6 is-offset-3" v-else>
            <Notification class="is-warning">
                {{ translate("page.admin_academy_results.no_results_published") }}
            </Notification>
        </div>
    </div>
    <div class="columns is-mobile is-vcentered is-centered" v-else>
        <LoadingSpinner2 class="has-added-margin" />
    </div>
</template>

<script setup lang="ts">
    import { ref, onMounted, computed } from "vue";
    import { translate } from "../Common/translations";
    import { handleError } from "../Common/errorHandler";
    import { autoConvertToTimezone } from "../Common/dateTimeUtils";
    import LoadingSpinner2 from "../Common/LoadingSpinner2.vue";
    import LoadingButton from "../Common/LoadingButton.vue";
    import Notification from "../Common/Notification.vue";
    import competitorResultsApi, { SharedAcademyResultWithCriteria } from "../Api/competitorResultsApi";
    import { successToast } from "../Common/toasts";
    import { useAcademyResultsStore } from "./academyResultsStore";
    import ChosenRulesView from "./CalculationRules/Viewing/ChosenRulesView.vue";
    import Accordion from "../Common/Accordion.vue";

    const store = useAcademyResultsStore();

    const initDone = ref(false);
    const publishedAcademyResults = ref<SharedAcademyResultWithCriteria[]>([]);
    const deletingInProgress = ref(0);

    const eventId = computed(() => store.eventId);

    const fetchPublishedAcademyResults = () => {
        return competitorResultsApi.getPublishedAcademyResults(eventId.value).then(result => {
            publishedAcademyResults.value = result;
        });
    };

    const deletePublishedResult = (id: number) => {
        if (!confirm(translate("common.are_you_sure"))) {
            return;
        }

        deletingInProgress.value = id;

        return competitorResultsApi
            .deletePublishedAcademyResult(eventId.value, id)
            .then(() => {
                successToast(translate("page.admin_academy_results.shared_result_deleted"));
                return fetchPublishedAcademyResults();
            })
            .catch(handleError)
            .finally(() => {
                deletingInProgress.value = 0;
            });
    };

    onMounted(() => {
        publishedAcademyResults.value = [];
        initDone.value = false;

        return fetchPublishedAcademyResults()
            .catch(handleError)
            .finally(() => {
                initDone.value = true;
            });
    });
</script>

<style scoped></style>
